import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BUTTON_SAVE } from '../../../shared/message/dialogs/default-form-dialog.component';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import * as _ from 'underscore';
import { IExtendedWindow, IObject } from '../../../../type_definitions/datatypes';
import { TranslationService } from '../../../shared/translation/translation.service';
import { BPProfilesDataService, IProfileReport } from '../bp-profiles-data.service';
import { BPProfilesViewCode } from '../bp-profiles.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { IDialogButton } from '../../../shared/message/ga-message.service';
import { UntypedFormGroup } from '@angular/forms';

interface IBPProfilesPossibleReport {
  id:string;
  checked?:boolean;
  label:string;
  viewCode:string;
}

@Component({
  selector: 'bp-profiles-report-dialog',
  template: `
    <default-dialog [title]="title" [buttons]="buttons" [closeButton]="true" [loading]="loading" (onClose)="onClose.emit()">
      <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>

      <form novalidate [action]="printUrl" target="_blank" method="post" #profilesOverviewForm>
        <formly [form]="form" [fields]="fields" [model]="model"></formly>
        <input type="hidden" *ngFor="let possibleReport of possibleReports" [id]="possibleReport.id" [name]="possibleReport.id" [value]="model[possibleReport.id] ? '1' : '0'"/>
        <input type="hidden" id="Deccoid" name="Deccoid" [value]="companyCountryId"/>
      </form>
    </default-dialog>
  `
})
export class BPProfilesReportDialogComponent implements OnInit {
  @Input() public profileId:number;
  @Input() public companyCountryId:number;
  @Output() public onClose:EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('profilesOverviewForm') private profilesOverviewForm:ElementRef<HTMLFormElement>;

  public title:string;
  public printUrl:string;
  public loading:boolean;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[] = [];
  public model:IObject = {};
  public buttons:IDialogButton[];

  public possibleReports:IBPProfilesPossibleReport[] = [
    {
      id: 'display_GenInfo',
      checked: true,
      label: this.translationService.instant('BPPROFILES.GENERAL_INFO.TEXT'),
      viewCode: BPProfilesViewCode.GENERAL_INFO
    },
    {
      id: 'display_BankInfo',
      checked: true,
      label: this.translationService.instant('BPPROFILES.BANK.TEXT'),
      viewCode: BPProfilesViewCode.BANK_INFO
    },
    {
      id: 'display_Access',
      checked: true,
      label: this.translationService.instant('BPPROFILES.ACCESS_SETTINGS.TEXT'),
      viewCode: BPProfilesViewCode.ACCESS_SETTINGS
    },
    {
      id: 'display_Roles',
      checked: true,
      label: this.translationService.instant('BPPROFILES.CONTACTS.TEXT'),
      viewCode: BPProfilesViewCode.CONTACTS
    },
    {
      id: 'display_Locations',
      checked: true,
      label: this.translationService.instant('BPPROFILES.LOCATIONS.TEXT'),
      viewCode: BPProfilesViewCode.LOCATIONS
    },
    {
      id: 'display_SLA',
      checked: true,
      label: this.translationService.instant('BPPROFILES.SLA.TEXT'),
      viewCode: BPProfilesViewCode.SLA
    },
    {
      id: 'display_TC',
      checked: true,
      label: this.translationService.instant('BPPROFILES.TC.TEXT'),
      viewCode: BPProfilesViewCode.TC
    },
    {
      id: 'display_CAVEATS',
      checked: true,
      label: this.translationService.instant('BPPROFILES.CAVEATS.TEXT'),
      viewCode: BPProfilesViewCode.CAVEATS
    },
    {
      id: 'display_Tier',
      checked: true,
      label: this.translationService.instant('BPPROFILES.OEM.TEXT'),
      viewCode: BPProfilesViewCode.OEM
    },
    {
      id: 'display_EngRates',
      checked: true,
      label: this.translationService.instant('BPPROFILES.ENGINEER_RATES.TEXT'),
      viewCode: BPProfilesViewCode.CATALOG
    },
    {
      id: 'display_Accred',
      checked: true,
      label: this.translationService.instant('BPPROFILES.VENDOR_ACCREDITATIONS.TEXT'),
      viewCode: BPProfilesViewCode.ACCREDITATIONS
    },
    {
      id: 'display_LocServ',
      checked: true,
      label: this.translationService.instant('BPPROFILES.LOCAL_SERVICES.TEXT'),
      viewCode: BPProfilesViewCode.CATALOG
    },
    {
      id: 'display_Docs',
      checked: true,
      label: this.translationService.instant('BPPROFILES.DOCUMENTS.TEXT'),
      viewCode: BPProfilesViewCode.DOCUMENTS
    },
    {
      id: 'display_ACSLInfo',
      checked: false,
      label: this.translationService.instant('BPPROFILES.ACSL_INFO.TEXT'),
      viewCode: BPProfilesViewCode.ACSL_INFO
    },
    {
      id: 'display_Contract',
      checked: false,
      label: this.translationService.instant('BPPROFILES.CONTRACT.TEXT'),
      viewCode: BPProfilesViewCode.CONTRACT
    }
  ];

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private formlyService:GaFormlyService,
    private bpProfilesDataService:BPProfilesDataService,
    private authService:AuthService,
    private translationService:TranslationService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.title = this.translationService.instant('BPPROFILES.PRINT_OVERVIEW.TITLE');
    this.buttons = [{
      id: BUTTON_SAVE,
      label: 'BPPROFILES.PRINT_OVERVIEW.PRINT_REPORT',
      className: 'btn btn-success',
      onClick: this.onPrintReport,
      busy: false,
      iconClass: 'fa-external-link'
    }, {
      label: 'BPPROFILES.PRINT_OVERVIEW.CANCEL_PRINT',
      className: 'btn btn-danger',
      onClick: () => this.onClose.emit()
    }];

    // prepare possible reports
    this.possibleReports = _.filter(this.possibleReports, (report:IBPProfilesPossibleReport) => this.authService.viewRead(report.viewCode));

    this.loading = true;
    this.bpProfilesDataService.getProfileReport(this.profileId, this.model)
      .then((data:IProfileReport) => {
        this.printUrl = this.authService.authenticatedUrl(data.reportLink);
        this.initModel();
        this.fields = this.createFields();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private initModel():void {
    this.model = {
      info: this.translationService.instant('BPPROFILES.PRINT_OVERVIEW.INFO')
    };

    _.each(this.possibleReports, (possibleReport:IBPProfilesPossibleReport) => {
      this.model[possibleReport.id] = possibleReport.checked;
    });
  }

  private onPrintReport = ():void => {
    // because the URL is in GA1, and we need to open this on new window (target = _blank)
    // we do form submission here, and we cannot do it in print report button because the button is not the same level hierarchy as the form is
    this.profilesOverviewForm.nativeElement.submit();
    // close the window immediately as the report will be open in new tab
    this.onClose.emit();
  };

  private createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';

    return [
      this.formlyService.aValue('info')
        .withoutWrapper()
        .withWrapperClass(fullWidthClass)
        .build()
    ].concat(
      _.map(this.possibleReports, (possibleReport:IBPProfilesPossibleReport) => {
        return this.formlyService.aCheckbox(possibleReport.id, possibleReport.label)
          .withoutWrapper()
          .withWrapperClass(fullWidthClass)
          .build();
      })
    );
  }
}